import { IGameplayEvent } from '../../../Core/State/IJoinedRoomState';
import { PokerAudioPresentation } from './PokerAudioPresentation';
import {
    COMMUNITY_CARD_ANIMATION_TOTAL_MS,
    COMMUNITY_CARD_TIMING,
    getCommunityCardIndexesForStreet,
    WINNER_INDICATOR_DISPLAY_MS
} from './PokerPresentationConfig';

interface RoomIdentity {
    roomId: string;
    gameId: string;
    handId?: string;
}

interface PokerGameplayPresentationCallbacks {
    getRoomIdentity: () => RoomIdentity;
    setActionBadge: (event: IGameplayEvent) => void;
    clearActionBadge: (userId: string | undefined) => void;
    clearActionBadges: () => void;
    clearNonTerminalActionBadges: () => void;
    setWinnerIndicators: (event: IGameplayEvent) => void;
    clearWinnerIndicators: () => void;
    clearCommunityCards: () => void;
    clearCompletedHandPresentation: (playerCardsVisibleAfterClear?: boolean) => void;
    revealCommunityCard: (placement: CommunityCardPlacement) => boolean;
    setAnimatedCommunityCardDelaysMs: (delays: { [cardIndex: number]: number }) => void;
    setShowdownCardsRevealKey: (revealKey: string | null) => void;
    setPlayerCardsVisible: (visible: boolean) => void;
}

export interface CommunityCardPlacement {
    key: string;
    roomId: string;
    gameId: string;
    handId: string;
    cardIndex: number;
    card: string;
}

interface HandResetOptions {
    fadeCompletedHandPresentation?: boolean;
    playerCardsVisibleAfterClear?: boolean;
}

export class PokerGameplayPresentation {
    private communityCardRevealTimer: NodeJS.Timeout | null = null;
    private winnerIndicatorTimeout: NodeJS.Timeout | null = null;
    private communityCardPlacementSoundTimers: Map<string, NodeJS.Timeout> = new Map();
    private communityCardAnimationClearTimers: Map<number, NodeJS.Timeout> = new Map();
    private handledCommunityCardPlacementKeys: Set<string> = new Set();
    private pendingCommunityCardPlacements: CommunityCardPlacement[] = [];
    private animatedCommunityCardDelaysMs: { [cardIndex: number]: number } = {};
    private lastCommunityCardRevealAt: number | null = null;

    constructor(
        private readonly callbacks: PokerGameplayPresentationCallbacks,
        private readonly audioManager: PokerAudioPresentation
    ) {}

    public handleGameplayEvent = (event: IGameplayEvent) => {
        switch (event.kind) {
            case 'handStarted':
                this.resetHand({
                    fadeCompletedHandPresentation: true,
                    playerCardsVisibleAfterClear: true
                });
                this.callbacks.clearActionBadges();
                this.audioManager.playGameplayEvent(event);
                break;
            case 'playerAction':
                this.callbacks.setActionBadge(event);
                this.audioManager.playGameplayEvent(event);
                break;
            case 'streetDealt':
                this.callbacks.clearNonTerminalActionBadges();
                this.applyStreetDealtEvent(event);
                break;
            case 'showdownStarted':
                this.callbacks.clearNonTerminalActionBadges();
                this.audioManager.playGameplayEvent(event);
                break;
            case 'showdownCardsRevealed':
                this.applyShowdownCardsRevealedEvent(event);
                this.audioManager.playGameplayEvent(event);
                break;
            case 'winnerDeclared':
                this.handleWinnerDeclaredEvent(event);
                break;
            case 'turnStarted':
                this.callbacks.clearActionBadge(event.actorUserId);
                this.audioManager.playGameplayEvent(event);
                break;
            case 'handEnded':
                this.clearCompletedHandPresentation();
                this.callbacks.clearActionBadges();
                this.audioManager.playGameplayEvent(event);
                break;
            default:
                this.audioManager.playGameplayEvent(event);
                break;
        }
    };

    public clearActionBadgeForTurnBoundary = (userId: string | undefined) => {
        this.callbacks.clearActionBadge(userId);
    };

    public resetHand = (options: HandResetOptions = {}) => {
        this.clearCommunityCardRevealTimer();
        this.clearCommunityCardPlacementSoundTimers();
        this.clearCommunityCardAnimationClearTimers();
        this.clearWinnerIndicatorTimeout();
        this.pendingCommunityCardPlacements = [];
        this.handledCommunityCardPlacementKeys.clear();
        this.animatedCommunityCardDelaysMs = {};
        this.lastCommunityCardRevealAt = null;
        this.callbacks.setAnimatedCommunityCardDelaysMs({});
        this.callbacks.setShowdownCardsRevealKey(null);
        if (options.fadeCompletedHandPresentation) {
            this.callbacks.clearCompletedHandPresentation(options.playerCardsVisibleAfterClear);
        } else {
            this.callbacks.clearWinnerIndicators();
            this.callbacks.clearCommunityCards();
            this.callbacks.setPlayerCardsVisible(options.playerCardsVisibleAfterClear ?? true);
        }
        this.audioManager.stopCommunityCardPlacementAudio();
    };

    public resetRoom = () => {
        this.resetHand();
        this.clearWinnerIndicatorTimeout();
        this.audioManager.reset();
    };

    public dispose = () => {
        this.resetRoom();
    };

    private applyStreetDealtEvent = (event: IGameplayEvent) => {
        const indexes = getCommunityCardIndexesForStreet(event.street);
        if (indexes.length === 0 || !event.cards || indexes.length !== event.cards.length) return;

        indexes.forEach((cardIndex, cardOffset) => {
            const placement: CommunityCardPlacement = {
                key: `${event.eventId}:${cardIndex}`,
                roomId: event.roomId,
                gameId: event.gameId,
                handId: event.handId,
                cardIndex,
                card: event.cards![cardOffset]
            };
            if (!this.handledCommunityCardPlacementKeys.has(placement.key)) {
                this.pendingCommunityCardPlacements.push(placement);
            }
        });

        this.scheduleNextCommunityCardPlacement(0);
    };

    private applyShowdownCardsRevealedEvent = (event: IGameplayEvent) => {
        this.callbacks.setShowdownCardsRevealKey(this.getGameplayEventHandKey(event));
        this.callbacks.setPlayerCardsVisible(true);
    };

    private handleWinnerDeclaredEvent = (event: IGameplayEvent) => {
        this.callbacks.clearActionBadges();
        this.callbacks.setWinnerIndicators(event);
        this.scheduleWinnerIndicatorClear(event);
        this.audioManager.playGameplayEvent(event);
    };

    private scheduleWinnerIndicatorClear = (event: IGameplayEvent) => {
        this.clearWinnerIndicatorTimeout();
        const handKey = this.getGameplayEventHandKey(event);
        this.winnerIndicatorTimeout = setTimeout(() => {
            this.winnerIndicatorTimeout = null;
            if (this.getCurrentHandKey() !== handKey) return;

            this.clearCompletedHandPresentation();
        }, WINNER_INDICATOR_DISPLAY_MS);
    };

    private clearCompletedHandPresentation = () => {
        this.clearCommunityCardRevealTimer();
        this.clearCommunityCardPlacementSoundTimers();
        this.clearCommunityCardAnimationClearTimers();
        this.pendingCommunityCardPlacements = [];
        this.animatedCommunityCardDelaysMs = {};
        this.lastCommunityCardRevealAt = null;
        this.callbacks.clearCompletedHandPresentation();
        this.audioManager.stopCommunityCardPlacementAudio();
    };

    private scheduleNextCommunityCardPlacement = (delayMs: number) => {
        if (this.communityCardRevealTimer || this.pendingCommunityCardPlacements.length === 0) return;

        this.communityCardRevealTimer = setTimeout(() => {
            this.communityCardRevealTimer = null;
            this.revealNextCommunityCardPlacement();
        }, this.getNextCommunityCardRevealDelay(delayMs));
    };

    private revealNextCommunityCardPlacement = () => {
        const placement = this.pendingCommunityCardPlacements.shift();
        if (!placement) return;

        const identity = this.callbacks.getRoomIdentity();
        if (placement.roomId !== identity.roomId) {
            this.scheduleNextCommunityCardPlacement(0);
            return;
        }

        if (this.callbacks.revealCommunityCard(placement)) {
            this.lastCommunityCardRevealAt = Date.now();
            this.handledCommunityCardPlacementKeys.add(placement.key);
            this.setCommunityCardAnimating(placement.cardIndex);
            this.scheduleCommunityCardPlacementSound(placement);
            this.scheduleCommunityCardAnimationClear(placement.cardIndex);
        }

        this.scheduleNextCommunityCardPlacement(COMMUNITY_CARD_TIMING.revealCadenceMs);
    };

    private getNextCommunityCardRevealDelay = (requestedDelayMs: number): number => {
        if (this.lastCommunityCardRevealAt === null) return requestedDelayMs;

        const elapsedSinceLastRevealMs = Date.now() - this.lastCommunityCardRevealAt;
        const remainingCadenceMs = Math.max(0, COMMUNITY_CARD_TIMING.revealCadenceMs - elapsedSinceLastRevealMs);
        return Math.max(requestedDelayMs, remainingCadenceMs);
    };

    private setCommunityCardAnimating = (cardIndex: number) => {
        this.animatedCommunityCardDelaysMs = {
            ...this.animatedCommunityCardDelaysMs,
            [cardIndex]: 0
        };
        this.callbacks.setAnimatedCommunityCardDelaysMs(this.animatedCommunityCardDelaysMs);
    };

    private scheduleCommunityCardPlacementSound = (placement: CommunityCardPlacement) => {
        if (this.communityCardPlacementSoundTimers.has(placement.key)) return;

        const timeout = setTimeout(() => {
            this.communityCardPlacementSoundTimers.delete(placement.key);
            const identity = this.callbacks.getRoomIdentity();
            if (placement.roomId !== identity.roomId) return;

            this.audioManager.playCommunityCardPlacement();
        }, COMMUNITY_CARD_TIMING.moveDurationMs);

        this.communityCardPlacementSoundTimers.set(placement.key, timeout);
    };

    private scheduleCommunityCardAnimationClear = (cardIndex: number) => {
        const existingTimer = this.communityCardAnimationClearTimers.get(cardIndex);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        const timeout = setTimeout(() => {
            this.communityCardAnimationClearTimers.delete(cardIndex);
            const nextDelays = { ...this.animatedCommunityCardDelaysMs };
            delete nextDelays[cardIndex];
            this.animatedCommunityCardDelaysMs = nextDelays;
            this.callbacks.setAnimatedCommunityCardDelaysMs(nextDelays);
        }, COMMUNITY_CARD_ANIMATION_TOTAL_MS + 100);

        this.communityCardAnimationClearTimers.set(cardIndex, timeout);
    };

    private clearCommunityCardRevealTimer = () => {
        if (this.communityCardRevealTimer) {
            clearTimeout(this.communityCardRevealTimer);
            this.communityCardRevealTimer = null;
        }
    };

    private clearCommunityCardPlacementSoundTimers = () => {
        this.communityCardPlacementSoundTimers.forEach((timeout) => clearTimeout(timeout));
        this.communityCardPlacementSoundTimers.clear();
    };

    private clearCommunityCardAnimationClearTimers = () => {
        this.communityCardAnimationClearTimers.forEach((timeout) => clearTimeout(timeout));
        this.communityCardAnimationClearTimers.clear();
    };

    private clearWinnerIndicatorTimeout = () => {
        if (this.winnerIndicatorTimeout) {
            clearTimeout(this.winnerIndicatorTimeout);
            this.winnerIndicatorTimeout = null;
        }
    };

    private getGameplayEventHandKey = (event: Pick<IGameplayEvent, 'roomId' | 'gameId' | 'handId'>): string => {
        return `${event.roomId}:${event.gameId}:${event.handId}`;
    };

    private getCurrentHandKey = (): string => {
        const identity = this.callbacks.getRoomIdentity();
        return `${identity.roomId}:${identity.gameId}:${identity.handId}`;
    };

}
